import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PostGroup, Post } from '../../services/postService';
import { X, Facebook, Instagram, Twitter, Linkedin, Youtube, Trash2, Eye, Sparkles, Filter } from 'lucide-react';
import { MediaFile } from '../media/types';
import mediaService from '../../services/mediaService';
import { useClient } from '../../contexts/ClientContext';
import moment from 'moment-timezone';
import SocialPostPreview from './SocialPostPreview';
import SimplePostEditModal from '../post/SimplePostEditModal';
import { useToast } from '@/hooks/use-toast';
import postService from '../../services/postService';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import SchedulePostModal from '../schedule/SchedulePostModal';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { useCalendarStore } from '../../stores/calendarStore';
import { UpgradeModal } from '../modals/UpgradeModal';
import PlatformIcon from '../common/PlatformIcon';

interface PostGroupPreviewModalProps {
  postGroup: PostGroup;
  onClose: () => void;
  initialPlatformFilter?: string;
  onDeleteSuccess: () => void;
  onEditPost?: (post: Post) => void;
}

const PostGroupPreviewModal: React.FC<PostGroupPreviewModalProps> = ({ postGroup, onClose, initialPlatformFilter, onDeleteSuccess, onEditPost }) => {
  const { currentClient } = useClient();
  const { toast } = useToast();
  const [postMedia, setPostMedia] = useState<{ [postId: string]: MediaFile[] }>({});
  const [isLoadingMedia, setIsLoadingMedia] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(initialPlatformFilter || null);
  const [currentPostGroup, setCurrentPostGroup] = useState<PostGroup>(postGroup);

  // Sync currentPostGroup with prop changes
  useEffect(() => {
    setCurrentPostGroup(postGroup);
  }, [postGroup]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [postIdToDelete, setPostIdToDelete] = useState<string | null>(null);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [publishingPostId, setPublishingPostId] = useState<string | null>(null);
  const [currentPublishingPlatform, setCurrentPublishingPlatform] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState<Post | null>(null);
  const queryClient = useQueryClient();
  const removeCalendarEvent = useCalendarStore((state) => state.removeEvent);
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalData, setUpgradeModalData] = useState<{
    message: string;
    currentCount?: number;
    limit?: number;
  }>({ message: '' });

  useEffect(() => {
    const fetchMedia = async () => {
      if (!currentClient) return;

      const mediaMap: { [postId: string]: MediaFile[] } = {};
      for (const post of currentPostGroup.posts) {
        if (post.media_files && post.media_files.length > 0) {
          try {
            const fetchedFiles = await Promise.all(
              post.media_files.map(fileId => mediaService.getMediaFile(fileId, currentClient.id))
            );
            mediaMap[post.id] = fetchedFiles;
          } catch (error) {
            console.error(`Failed to fetch media for post ${post.id}:`, error);
          }
        } else {
          mediaMap[post.id] = [];
        }
      }
      setPostMedia(mediaMap);
      setIsLoadingMedia(false);
    };

    fetchMedia();
  }, [currentPostGroup, currentClient]);

  const uniquePlatforms = Array.from(new Set(currentPostGroup.posts.flatMap(post => post.platforms || [])));

  const handleDeletePost = async (postId: string) => {
    console.log('handleDeletePost called with postId:', postId);
    
    if (!currentClient) {
      console.log('No current client');
      return;
    }

    // Show confirmation by setting the deleting state
    if (deletingPostId === postId) {
      // Second click - actually delete
      console.log('Confirming delete for post:', postId);
      try {
        console.log('Calling postService.deletePost with:', postId);
        await postService.deletePost(postId);
        console.log('Post deleted successfully');
        
        toast({
          title: "Post Deleted",
          description: "The post has been deleted successfully.",
        });
        
        const updatedPosts = currentPostGroup.posts.filter(post => post.id !== postId);
        console.log('Updated posts after deletion:', updatedPosts);
        setCurrentPostGroup({ ...currentPostGroup, posts: updatedPosts });

        if (updatedPosts.length === 0) {
          console.log('No posts left, deleting entire post group');
          try {
            await postService.deletePostGroup(currentPostGroup.id);
            console.log('Post group deleted successfully');
            toast({
              title: "Post Group Deleted",
              description: "The post group has been deleted as it had no remaining posts.",
            });
          } catch (groupDeleteError) {
            console.error('Failed to delete post group:', groupDeleteError);
            toast({
              title: "Warning",
              description: "Post was deleted but failed to delete the empty post group.",
              variant: "destructive",
            });
          }
          onClose();
          onDeleteSuccess();
        } else {
          console.log('Posts remaining, calling onDeleteSuccess');
          onDeleteSuccess();
        }

      } catch (error) {
        console.error('Failed to delete post:', error);
        toast({
          title: "Deletion Failed",
          description: `An error occurred while deleting the post: ${error.response?.data?.detail || error.message}`,
          variant: "destructive",
        });
      } finally {
        setDeletingPostId(null);
      }
    } else {
      // First click - show confirmation
      console.log('Setting delete confirmation for post:', postId);
      setDeletingPostId(postId);
      // Auto-cancel after 3 seconds
      setTimeout(() => {
        setDeletingPostId(null);
      }, 3000);
    }
  };

  const confirmDeletePost = async () => {
    console.log('confirmDeletePost called');
    console.log('currentClient:', currentClient);
    console.log('postIdToDelete:', postIdToDelete);
    
    if (!currentClient || !postIdToDelete) {
      console.log('Early return - missing client or postId');
      return;
    }

    try {
      console.log('Calling postService.deletePost with:', postIdToDelete);
      const result = await postService.deletePost(postIdToDelete);
      console.log('Post deleted successfully, result:', result);
      
      toast({
        title: "Post Deleted",
        description: "The post has been deleted successfully.",
      });
      
      const updatedPosts = currentPostGroup.posts.filter(post => post.id !== postIdToDelete);
      console.log('Updated posts after deletion:', updatedPosts);
      console.log('Original posts count:', currentPostGroup.posts.length);
      console.log('New posts count:', updatedPosts.length);
      
      setCurrentPostGroup({ ...currentPostGroup, posts: updatedPosts });

      if (updatedPosts.length === 0) {
        console.log('No posts left, closing modal');
        onClose();
        onDeleteSuccess();
      } else {
        console.log('Posts remaining, calling onDeleteSuccess');
        onDeleteSuccess();
      }

    } catch (error) {
      console.error('Failed to delete post:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast({
        title: "Deletion Failed",
        description: `An error occurred while deleting the post: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    } finally {
      console.log('Cleaning up dialog state');
      setShowConfirmDialog(false);
      setPostIdToDelete(null);
    }
  };

  const handlePublishPost = async (post: Post) => {
    const media = postMedia[post.id];
    const imageUrls = media?.map(file => file.file_url) || [];
    
    const platforms = post.platforms || ['instagram'];
    const hasInstagram = platforms.includes('instagram');
    const hasFacebook = platforms.includes('facebook');
    
    // Instagram requires media, Facebook can be text-only
    if (hasInstagram && (!media || media.length === 0)) {
      toast({ 
        title: "No Media", 
        description: "Instagram posts require at least one image.", 
        variant: "destructive" 
      });
      return;
    }
    
    setPublishingPostId(post.id);
    
    try {
      // Publish to platforms sequentially to show progress
      const successful: string[] = [];
      const failed: { platform: string; error: any }[] = [];
      
      for (const platform of platforms) {
        setCurrentPublishingPlatform(platform);
        
        try {
          await postService.publishPost(post.id, imageUrls, post.content, platform);
          successful.push(platform);
        } catch (error) {
          failed.push({ platform, error });
        }
      }
      
      // Clear current platform
      setCurrentPublishingPlatform(null);
      
      if (successful.length > 0 && failed.length === 0) {
        // All platforms succeeded
        const platformNames = successful.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
        toast({ 
          title: "Post Published", 
          description: `The post has been published successfully to ${platformNames}.` 
        });
      } else if (successful.length > 0 && failed.length > 0) {
        // Some platforms succeeded, some failed
        const successNames = successful.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
        const failedNames = failed.map(f => f.platform.charAt(0).toUpperCase() + f.platform.slice(1)).join(', ');
        
        toast({ 
          title: "Partial Success", 
          description: `Published to ${successNames}. Failed to publish to ${failedNames}.`,
          variant: "destructive"
        });
      } else {
        // All platforms failed - get the first error for display
        throw failed[0].error;
      }
      
      onDeleteSuccess(); // To refetch and update the UI
    } catch (error: any) {
      console.error('Failed to publish post:', error);
      
      // Check if it's a tier limit error
      if (error?.response?.status === 403 && error?.response?.data?.detail) {
        const errorDetail = error.response.data.detail;
        
        // Check if it's a monthly post limit error
        if (typeof errorDetail === 'object' && errorDetail.error === 'monthly_post_limit_exceeded') {
          setUpgradeModalData({
            message: errorDetail.message,
            currentCount: errorDetail.current_count,
            limit: errorDetail.limit
          });
          setShowUpgradeModal(true);
        } else {
          const message = typeof errorDetail === 'string' ? errorDetail : errorDetail.message || 'Publishing failed';
          toast({ 
            title: "Publish Failed", 
            description: message, 
            variant: "destructive" 
          });
        }
      } else {
        toast({ 
          title: "Publish Failed", 
          description: "An error occurred while publishing the post.", 
          variant: "destructive" 
        });
      }
    } finally {
      setPublishingPostId(null);
      setCurrentPublishingPlatform(null);
    }
  };

  const handleSchedulePost = (post: Post) => {
    setSelectedPost(post);
    setIsScheduleModalOpen(true);
  };

  const handleUnschedulePost = async (post: Post) => {
    try {
      await postService.updatePost(post.id, { status: 'draft', scheduled_at: undefined });
      toast({ title: "Post Unscheduled", description: "The post has been unscheduled successfully." });
      
      const updatedPosts = currentPostGroup.posts.map(p => 
        p.id === post.id ? { ...p, status: 'draft', scheduled_at: undefined } : p
      );
      setCurrentPostGroup({ ...currentPostGroup, posts: updatedPosts });
      
      // Invalidate all relevant queries to ensure calendar updates
      queryClient.invalidateQueries({ queryKey: ['scheduledPosts', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostGroups', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostsForQueue'] });
      queryClient.invalidateQueries({ queryKey: ['draftPosts'] });
      queryClient.invalidateQueries({ queryKey: ['postGroups'] });
      
      onDeleteSuccess();
    } catch (error) {
      console.error('Failed to unschedule post:', error);
      toast({ title: "Unschedule Failed", description: "An error occurred while unscheduling the post.", variant: "destructive" });
    }
  };

  const handleScheduleSuccess = (updatedPost: Post) => {
    const updatedPosts = currentPostGroup.posts.map(p =>
      p.id === updatedPost.id ? updatedPost : p
    );
    setCurrentPostGroup({ ...currentPostGroup, posts: updatedPosts });
    onDeleteSuccess(); // To refetch and update the UI
  };

  const handleEditPost = (post: Post) => {
    if (onEditPost) {
      onEditPost(post);
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setPostToEdit(null);
    onDeleteSuccess(); // To refetch and update the UI
    // Don't reopen the PostGroupPreviewModal
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-slate-900/95 backdrop-blur-2xl border-slate-200 dark:border-slate-600/50 w-full max-w-7xl max-h-[90vh] overflow-hidden">
        <DialogHeader className="relative">
          <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl blur-xl"></div>
          <div className="relative flex items-center space-x-4 p-6 bg-slate-100/30 dark:bg-slate-800/30 rounded-2xl border border-slate-200/50 dark:border-slate-600/30">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-3xl font-black bg-gradient-to-r from-gray-900 via-indigo-500 to-purple-500 bg-clip-text text-transparent dark:from-white dark:via-indigo-200 dark:to-purple-200">
                {currentPostGroup.title}
              </DialogTitle>
              <p className="text-slate-500 dark:text-slate-400 text-lg mt-1">Preview your social media posts</p>
            </div>
          </div>
        </DialogHeader>

        {uniquePlatforms.length > 1 && (
          <motion.div 
            className="flex justify-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur"></div>
              <div className="relative flex items-center space-x-2 bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl p-2 rounded-2xl border border-slate-200/50 dark:border-slate-600/30">
                <div className="flex items-center space-x-1 mr-2">
                  <Filter className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  <span className="text-sm text-slate-600 dark:text-slate-400 font-medium">Filter:</span>
                </div>
                
                <Button
                  variant="ghost"
                  onClick={() => setSelectedPlatform(null)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    selectedPlatform === null 
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' 
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 hover:text-slate-800 dark:hover:text-white'
                  }`}
                >
                  All Platforms
                </Button>
                
                {uniquePlatforms.map(platform => (
                  <Button
                    key={platform}
                    variant="ghost"
                    onClick={() => setSelectedPlatform(platform)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all duration-300 ${
                      selectedPlatform === platform 
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' 
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 hover:text-slate-800 dark:hover:text-white'
                    }`}
                  >
                    <PlatformIcon platform={platform} className="w-4 h-4" />
                    <span>{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                  </Button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        <div className="max-h-[60vh] overflow-y-auto px-2">
          {isLoadingMedia ? (
            <motion.div 
              className="flex flex-col items-center justify-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-slate-200 dark:border-slate-700/30 border-t-indigo-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" style={{ animationDelay: '0.15s' }}></div>
              </div>
              <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-200 mb-2">Loading previews</h3>
              <p className="text-slate-500 dark:text-slate-400">Preparing your social media posts...</p>
            </motion.div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <AnimatePresence mode="wait">
                {currentPostGroup.posts.map((post, index) => {
                  const isVisible = selectedPlatform === null || post.platforms?.includes(selectedPlatform);
                  
                  if (!isVisible) return null;
                  
                  return (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -20 }}
                      transition={{ 
                        duration: 0.4, 
                        delay: index * 0.1,
                        ease: "easeOut"
                      }}
                      className="relative group"
                    >
                      <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative">
                        <SocialPostPreview
                          post={post}
                          media={postMedia[post.id] || []}
                          onDelete={handleDeletePost}
                          onPublish={handlePublishPost}
                          onSchedule={handleSchedulePost}
                          onUnschedule={handleUnschedulePost}
                          onEdit={handleEditPost}
                          isPublishing={publishingPostId === post.id}
                          isDeleting={deletingPostId === post.id}
                          currentPublishingPlatform={publishingPostId === post.id ? currentPublishingPlatform : null}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </DialogContent>

      {isScheduleModalOpen && selectedPost && (
        <SchedulePostModal
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          post={selectedPost}
          onScheduleSuccess={handleScheduleSuccess}
        />
      )}
      
      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        title="Monthly Post Limit Reached"
        message={upgradeModalData.message}
        currentCount={upgradeModalData.currentCount}
        limit={upgradeModalData.limit}
        feature="posts"
      />

      {/* Edit modal removed - handled by parent component */}
    </Dialog>
  );
};

export default PostGroupPreviewModal;